/**
 * The account's own hub list (`GET /hubs`) — the evidence behind "is this
 * account a user of that hub?" (plan `docs/plans/LOCAL-FIRST-CONNECT.md` §2/D4).
 *
 * One identity, one list. Before this module the app answered that question by
 * walking its own storage for a credential filed under some *address spelling*
 * (`utils/hubBinding.ts` before the change, evidence §3 of the plan): the device
 * held an `owner` device credential for the Dev hub under the host-only loopback
 * key `torik_device_token:local:127.0.0.1/api`, the walk skipped that spelling,
 * and the launcher offered to adopt a hub the same account already owned.
 *
 * The account's list is the fact the defect was missing, and it is already one
 * round trip the launcher makes on every start (`components/server/ServersSection.vue`
 * → `GET /hubs`). This module remembers it so the *synchronous* readers
 * (`services/hubBinding.ts` → `isHubBound`, called once per card while a list
 * renders) can answer without a fetch of their own.
 *
 * Two id spellings, both kept, because the two ends of the wire use different
 * ones (measured 2026-09-24, plan evidence §7):
 *
 *   - `id`     — the **cloud row** (`GET /hubs` → `hubId`, e.g. `517e7adb-…`):
 *                what the launcher's cards hold and what `/hubs/:id/bindings`
 *                answers under;
 *   - `hubId`  — the hub's **own** id as the cloud recorded it (`macAddress`,
 *                e.g. `0cca3964-…`): what discovery announces and what mDNS and
 *                `GET /api/hub/identity` name. The launcher already merges its
 *                two card lists on exactly this pair.
 *
 * A row also carries the account's `publicId` for the hub when something has
 * read it (`services/hubAccountIdentity.ts` → `accountIdentityFor`, which fills
 * it in here); it is the sharper evidence when a responder names itself, and it
 * is never invented — `null` means "not read yet", not "no".
 *
 * Nothing here decides anything: the matching rule is
 * `services/hubBinding.ts` → `accountHubFor`, and the list is only as fresh as
 * the last load. A *failure* caches nothing (like `hubAccountIdentity`), so a
 * transient cloud error cannot be remembered as "this account holds no hubs" —
 * but the last good list survives one, because it lives on this device already:
 * every answer is written to the buffer (`services/serverProfiles.ts`, plan
 * §2/C2) and the buffer is what answers before the cloud does.
 */
import { ref } from 'vue';
import { getRuntimeConfig } from '../config/runtime';
import { bufferedServerProfiles, rememberCloudServerProfiles, rememberProfilePublicId } from './serverProfiles';

/** One row of the account's hub list, as the binding question needs it. */
export interface AccountHubRow {
  /** The id the cloud knows the hub by (`GET /hubs` → `hubId`). */
  id: string;
  /** The hub's own id as the cloud recorded it (`GET /hubs` → `macAddress`). */
  hubId: string | null;
  /** The account's own identity for the hub, when something has read it. */
  publicId: string | null;
  /**
   * The hub's Ed25519 public key, base64url — the key the cloud recorded when
   * the hub registered (`GET /hubs` → `publicKey`). Case matters: it is
   * base64url, so it is trimmed and never lowercased like the ids.
   *
   * The local-first route needs it (`services/hubAccountIdentity.ts`): the
   * deployed cloud serves `hubPublicId` on `/hubs/:id/bindings` but not
   * `hubPublicKey`, and the key in this list is the same key the hub signs with
   * — the id can be derived from it (F9, `utils/hubIdentity` →
   * `hubPublicIdFromKey`), so a connect does not have to wait for a cloud
   * release to prove the hub (card `t_515b4051` follow-up, 24.9.2026).
   */
  publicKey: string | null;
  /** The hub's name, for log lines. */
  name: string | null;
}

/** The account's hubs, as last loaded. Reactive: the cards read it while they render. */
export const accountHubs = ref<AccountHubRow[]>([]);

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length ? trimmed : null;
}

/**
 * The rows of a `GET /hubs` answer, with the two spellings of each hub's id.
 *
 * Pure, and tolerant of the answer's two shapes (`[…]` and `{hubs: […]}`), the
 * same way the launcher reads it — this is the one place that maps the payload,
 * so a second reader cannot pick a different field.
 */
export function readAccountHubRows(raw: unknown): AccountHubRow[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { hubs?: unknown }).hubs)
      ? (raw as { hubs: unknown[] }).hubs
      : [];
  const rows: AccountHubRow[] = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue;
    const hub = entry as Record<string, unknown>;
    const id = text(hub.hubId) ?? text(hub.id);
    if (!id) continue;
    rows.push({
      id,
      hubId: text(hub.macAddress),
      publicId: text(hub.hubPublicId),
      publicKey: keyText(hub.publicKey) ?? keyText(hub.hubPublicKey),
      name: typeof hub.name === 'string' && hub.name.trim() ? hub.name.trim() : null,
    });
  }
  return rows;
}

/**
 * A key, trimmed and **not** lowercased: base64url is case-sensitive, and a
 * lowercased key verifies nothing (the ids above are hex, so they can be).
 */
function keyText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

/**
 * The account's row for one hub, by either spelling of its id — the cloud's
 * (`GET /hubs` → `id`) or the hub's own (`→ macAddress`). Both name the same
 * device, and a caller may hold either.
 */
export function findAccountHubRow(hubId: string): AccountHubRow | null {
  const id = text(hubId);
  if (!id) return null;
  return accountHubs.value.find((row) => row.id === id || row.hubId === id) ?? null;
}

/**
 * Remember a freshly loaded list (the launcher already has one in hand).
 *
 * The same answer is written to this device's **buffer** (`services/serverProfiles.ts`,
 * plan §2/C2): it is the copy the launcher paints from before the next answer
 * arrives, and the one a restart without a cloud still has. `accountId` is the
 * cloud account the answer belongs to, when the caller knows it.
 */
export function rememberAccountHubs(raw: unknown, options: { accountId?: string | null } = {}): AccountHubRow[] {
  const rows = readAccountHubRows(raw);
  if (rows.length) {
    accountHubs.value = rows;
    rememberCloudServerProfiles(raw, { accountId: options.accountId });
  }
  return rows;
}

/**
 * The account's list as **this device** remembers it (plan §2/C2): the buffered
 * servers, mapped to the rows the binding question reads.
 *
 * This is what a caller gets at launch before the cloud answers — and what it
 * keeps answering with when the cloud cannot be reached at all.
 */
export function bufferedAccountRows(): AccountHubRow[] {
  const rows: AccountHubRow[] = [];
  for (const profile of bufferedServerProfiles()) {
    const id = profile.cloudId ?? profile.hubId;
    if (!id) continue;
    rows.push({
      id,
      hubId: profile.hubId,
      publicId: profile.publicId,
      publicKey: profile.publicKey,
      name: profile.name,
    });
  }
  return rows;
}

/**
 * Record the account's `publicId` for one hub, learned from
 * `GET /hubs/:id/bindings` (`services/hubAccountIdentity.ts`). A row that is not
 * in the list is left alone: the list is the account's, and this only sharpens
 * an entry that is already there.
 */
export function rememberAccountHubPublicId(hubId: string, publicId: string | null): void {
  const id = text(hubId);
  if (!id) return;
  const normalized = text(publicId);
  // The buffer keeps the same sharpening (plan §2/C2): the record a launch paints
  // from carries the id the LAN claim is gated on (`services/hubLocalFirst.ts`).
  rememberProfilePublicId(id, normalized);
  const rows = accountHubs.value;
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    if (row.id !== id && row.hubId !== id) continue;
    if ((row.publicId ?? null) === normalized) return;
    const next = rows.slice();
    next[index] = { ...row, publicId: normalized };
    accountHubs.value = next;
    return;
  }
}

/** The account signed out, or a test wants a clean slate. */
export function clearAccountHubs(): void {
  accountHubs.value = [];
}

/**
 * The account's hubs as of now (`accountHubs`), loading them when nothing has.
 *
 * Answers `[]` for a device with no cloud session: there is no account to ask,
 * and an empty list is the honest reading of that — every card then offers the
 * way in that does not need one (join, adopt, or the device credential of a
 * panel).
 *
 * **The buffer answers first** (plan §2/C2): before this function dials the cloud
 * — and without dialling it at all, when a previous answer is on this device —
 * the list it returns is what the last successful `GET /hubs` wrote
 * (`services/serverProfiles.ts`). The synchronous readers (`services/hubBinding.ts`
 * → `isHubBound`, called once per card while a list renders) therefore get a real
 * answer at launch instead of an empty one, and a *failure* keeps it: a transient
 * cloud error is still never remembered as "this account holds no hubs", but the
 * last good list survives it.
 */
export async function loadAccountHubs(options: { force?: boolean } = {}): Promise<AccountHubRow[]> {
  // Loaded here, not at import: this module is read by tests and by card
  // renderers, and the stores reach the socket and the WebRTC layer.
  const [{ useAuthStore }, { apiFetch }] = await Promise.all([import('../stores/auth'), import('./api')]);
  const auth = useAuthStore();
  if (!auth.isCloudAuthenticated && !auth.cloudAccessToken) {
    clearAccountHubs();
    return [];
  }

  // Hydrated from this device's own storage, synchronously, before any network
  // call — this is the whole point of the buffer (plan §2/C2).
  if (!accountHubs.value.length) {
    const buffered = bufferedAccountRows();
    if (buffered.length) accountHubs.value = buffered;
  }
  if (!options.force && accountHubs.value.length) return accountHubs.value;

  try {
    const tokenValid = await auth.ensureCloudToken();
    if (!tokenValid) return accountHubs.value;
    const data = await apiFetch<unknown>('/hubs', {
      authToken: auth.cloudAccessToken || undefined,
      baseUrl: getRuntimeConfig().cloudApiUrl,
      bypassRouting: true,
    });
    return rememberAccountHubs(data, { accountId: auth.cloudUser?.id ?? null });
  } catch (error) {
    // Not cached as an answer: the next caller asks again instead of inheriting
    // "no hubs". What the buffer holds is returned unchanged (plan §2/C2).
    console.warn("[AccountHubs] could not read the account's hub list:", error);
    return accountHubs.value;
  }
}

/** Tests only. */
export function resetAccountHubsForTests(): void {
  accountHubs.value = [];
}
